import {TourStep, tourStepDict} from "@/constants/tourSteps";
import {getSubRoot} from "@/utils/utils";
import {getValidSliderValue} from "@/lib/utils";

export const simulateSliderMovement = async (
    sliderContainer: HTMLElement,
    targetValue: number
) => {
    const rangeInput = sliderContainer.querySelector<HTMLInputElement>('input[type="range"]');

    if (!rangeInput) {
        console.error("Nessun input range trovato nello Slider:", sliderContainer);
        return;
    }

    const min = parseFloat(rangeInput.min) || 0;
    const max = parseFloat(rangeInput.max) || 100;
    const step = parseFloat(rangeInput.step) || 15;

    const finalValue = getValidSliderValue(targetValue, step, min, max);
    const startValue = parseFloat(rangeInput.value) || min;

    const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
    )?.set;

    const applyValue = (val: number) => {
        nativeSetter?.call(rangeInput, val.toString());
        rangeInput.dispatchEvent(new Event('input', { bubbles: true }));
        rangeInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    // Effetto trascinamento progressivo (se il valore è distante, sale/scende a step)
    const direction = finalValue >= startValue ? 1 : -1;
    let current = startValue;

    while ((direction === 1 && current < finalValue) || (direction === -1 && current > finalValue)) {
        current += direction * step;
        if ((direction === 1 && current > finalValue) || (direction === -1 && current < finalValue)) {
            current = finalValue;
        }
        applyValue(current);
        await new Promise((r) => setTimeout(r, 60)); // Pausa per l'animazione visiva
    }

    applyValue(finalValue);
};

const updateButtons = (isValid: boolean) => {
    const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLElement | null;
    const doneBtn = document.querySelector('.driver-popover-done-btn') as HTMLElement | null;

    [nextBtn, doneBtn].forEach(btn => {
        if (!btn) return;
        btn.toggleAttribute('disabled', !isValid);
        btn.classList.toggle('disabled', !isValid);
        btn.style.pointerEvents = isValid ? 'auto' : 'none';
        btn.style.opacity = isValid ? '1' : '0.4';
    });
};

// Helper per simulare la digitazione e il click nell'autocomplete + bottoni + dataranges
export const executeAgenticInputSequence = async (
    dataType: string,
    element: HTMLElement,
    value: string,
    isCertain: boolean,
    onComplete: () => void
): Promise<{data: string | null}> => {
    if(dataType==='tags_clip') {
        // Se l'agente non è certo, ci fermiamo qui (l'utente seleziona manualmente)
        if(!isCertain){
            return new Promise<{ data: string | null }>((resolve) => {

                // Controllo stato iniziale (eseguito UNA sola volta all'avvio dello step)
                const inputEl = (
                    element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
                        ? element
                        : element.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
                );

                updateButtons(Boolean(inputEl?.value.trim() ? inputEl?.value.trim().length > 0 ? true : false : false));

                const handleInput = (event: Event) => {
                    const target = event.target as HTMLInputElement | HTMLTextAreaElement;

                    // Verifichiamo solo che l'evento arrivi da un campo di testo
                    if (target) {
                        updateButtons(target.value.trim().length > 0);
                    }
                };

                const handleChange = (event: Event) => {
                    const target = event.target as HTMLInputElement | HTMLTextAreaElement;

                    //Dopo la verifica sul target, ritornarlo con resolve
                    if (target && target.value) {
                        resolve({data: target.value})
                    }
                }

                // Assegna l'evento al contenitore (sfrutta l'event bubbling da qualsiasi input interno)
                element.addEventListener('input', handleInput);

                element.addEventListener('change', handleChange);
            })
        }
        if(isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 400));

            simulateTyping(element, value, true)

            onComplete();
        }
        //Fallback data
        return {data:null};
    }
    else if (dataType === 'slider') {
        const rangeInput = element.querySelector<HTMLInputElement>('input[type="range"]');

        // ---------------------------------------------------------------------
        // CASO 1: !isCertain -> L'utente sposta lo slider manualmente
        // ---------------------------------------------------------------------
        if (!isCertain) {
            return new Promise<{ data: string | null }>((resolve) => {
                // Sblocca i bottoni se il cursore viene mosso rispetto al valore iniziale
                const initialVal = rangeInput?.value;

                const handleSliderChange = (event: Event) => {
                    const target = event.target as HTMLInputElement;
                    if (target && target.value !== initialVal) {
                        updateButtons(true);
                        resolve({ data: target.value });
                    }
                };

                element.addEventListener('change', handleSliderChange);
                element.addEventListener('input', handleSliderChange);
            });
        }

        // ---------------------------------------------------------------------
        // CASO 2: isCertain === true -> L'agente imposta il valore target
        // ---------------------------------------------------------------------
        if (isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const parsedNumericValue = parseFloat(value) || 0;

            await simulateSliderMovement(element, parsedNumericValue);

            await new Promise((resolve) => setTimeout(resolve, 500));
            onComplete();

            return { data: parsedNumericValue.toString() };
        }
    }
    else if(dataType==='tags') {
        // Se l'agente non è certo, ci fermiamo qui (l'utente seleziona manualmente)
        if(!isCertain){
            return new Promise<{ data: string | null }>((resolve) => {

                // Controllo stato iniziale (eseguito UNA sola volta all'avvio dello step)
                const inputEl = (
                    element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
                        ? element
                        : element.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
                );

                updateButtons(Boolean(inputEl?.value.trim() ? inputEl?.value.trim().length > 0 ? true : false : false));

                const handleInput = (event: Event) => {
                    const target = event.target as HTMLInputElement | HTMLTextAreaElement;

                    // Verifichiamo solo che l'evento arrivi da un campo di testo
                    if (target) {
                        updateButtons(target.value.trim().length > 0);
                    }
                };

                const handleChange = (event: Event) => {
                    const target = event.target as HTMLInputElement | HTMLTextAreaElement;

                    //Dopo la verifica sul target, ritornarlo con resolve
                    if (target && target.value) {
                        resolve({data: target.value})
                    }
                }

                // Assegna l'evento al contenitore (sfrutta l'event bubbling da qualsiasi input interno)
                element.addEventListener('input', handleInput);

                element.addEventListener('change', handleChange);
            })
        }
        if(isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 400));

            simulateTyping(element, value, true)

            await new Promise((resolve) => setTimeout(resolve, 400));

            const firstOption = document.querySelector('.autocomplete-option, [role="option"]') as HTMLElement | null;
            if (firstOption) {
                firstOption.click();
            }
            else {
                await new Promise((resolve) => setTimeout(resolve, 150));
                onComplete();
                return {data:null}
            }

            await new Promise((resolve) => setTimeout(resolve, 1000));

            await new Promise((resolve) => setTimeout(resolve, 150));
            onComplete();
        }
        //Fallback data
        return {data:null};
    }
    else if (dataType === 'radio') {
        // Helper per verificare se il rating ha già un valore selezionato
        const getSelectedRating = (): string | null => {
            const checkedRadio = element.querySelector<HTMLInputElement>('input[type="radio"]:checked');
            return checkedRadio ? checkedRadio.value : null;
        };

        if (!isCertain) {
            return new Promise<{ data: string | null }>((resolve) => {
                const checkRating = () => {
                    const currentVal = getSelectedRating();
                    const isValid = currentVal !== null && parseFloat(currentVal) > 0;

                    updateButtons(isValid);

                    if (isValid) {
                        resolve({ data: currentVal });
                    }
                };

                // Verifica iniziale (se già votato)
                checkRating();

                // Ascolta il cambio di selezione sulle stelle
                element.addEventListener('change', checkRating);
            });
        }

        if (isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Normalizza il valore (es. converte virgole in punti: "1,5" -> "1.5")
            const targetValue = String(value).trim().replace(',', '.');

            // 1. Cerca il radio input con il valore corrispondente dentro l'elemento
            let radio : any = null;
            if(targetValue === 'true' || targetValue === 'false')
                radio = element.querySelector<HTMLInputElement>(`input[type="radio"]`);
            else
                radio = element.querySelector<HTMLInputElement>(`input[type="radio"][value="${targetValue}"]`);

            if (radio && (value === undefined || value && value!=='false')) {
                // 2. In MUI la label collegata via 'for' intercetta le coordinate visive e il focus
                const associatedLabel = radio.id
                    ? element.querySelector<HTMLElement>(`label[for="${CSS.escape(radio.id)}"]`)
                    : null;

                // Clicca la label (preferenziale per MUI) o direttamente il radio input
                const clickable = associatedLabel || radio;
                clickable.click();

                // Fallback per forzare l'evento change se il componente è controllato
                if (!radio.checked) {
                    radio.checked = true;
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 500));
            onComplete();

            return { data: targetValue };
        }
    }
    else if (dataType === 'button') {
        // Normalizzazione stringhe per match agnostico (ignora maiuscole, spazi, underscore e trattini)
        const cleanStr = (s: string) => s.toLowerCase().replace(/[\s_\u200B\-\/\.]/g, '');

        // Verifica se una specifica checkbox (o il suo contenitore) risulta già spuntata
        const isTargetChecked = (target: HTMLElement): boolean => {
            if (target instanceof HTMLInputElement && target.type === 'checkbox') {
                return target.checked;
            }
            const innerInput = target.querySelector<HTMLInputElement>('input[type="checkbox"]');
            if (innerInput) return innerInput.checked;

            return (
                target.classList.contains('Mui-checked') ||
                target.getAttribute('aria-checked') === 'true' ||
                !!target.querySelector('.Mui-checked, [aria-checked="true"]')
            );
        };

        // Esegue il click solo se non è già attiva (evita deselezioni accidentali)
        const checkTarget = (target: HTMLElement) => {
            if (isTargetChecked(target)) return;

            // In MUI cliccare la label o il componente root è l'azione più affidabile per triggerare onChange
            const clickable = (target instanceof HTMLInputElement)
                ? (target.closest('label') || target)
                : (target.querySelector<HTMLElement>('input, label, .MuiButtonBase-root') || target);

            clickable.click();
        };

        // Estrae ESCLUSIVAMENTE le checkbox interne a questo `element` (o se stesso)
        const findCheckboxesInsideHighlightedElement = (root: HTMLElement): HTMLElement[] => {
            // Caso 1: L'elemento evidenziato dallo step è già la singola checkbox
            if (
                (root instanceof HTMLInputElement && root.type === 'checkbox') ||
                root.classList.contains('MuiCheckbox-root') ||
                root.getAttribute('role') === 'checkbox' ||
                root.getAttribute('data-tour')?.startsWith('field_checkbox_filter')
            ) {
                return [root];
            }

            // Caso 2: L'elemento evidenziato è il box contenitore.
            // Cerchiamo le opzioni ESCLUSIVAMENTE tra i suoi figli
            const dataTourOptions = Array.from(root.querySelectorAll<HTMLElement>('[data-tour^="field_checkbox_filter"]'));
            if (dataTourOptions.length > 0) {
                return dataTourOptions;
            }

            // Fallback per checkbox native o MUI generiche interne al box
            const nativeCheckboxes = Array.from(root.querySelectorAll<HTMLElement>('input[type="checkbox"], [role="checkbox"]'));
            return nativeCheckboxes;
        };
        if (!isCertain) {
            return new Promise<{ data: string | null }>((resolve) => {
                const hasSelectionInside = () => {
                    const checkboxes = findCheckboxesInsideHighlightedElement(element);
                    return checkboxes.some(isTargetChecked);
                };

                const handleUserInteraction = () => {
                    const isValid = hasSelectionInside();
                    updateButtons(isValid);

                    if (isValid) {
                        resolve({ data: 'manually_selected' });
                    }
                };

                handleUserInteraction();

                // Ascolta gli eventi solo all'interno dell'elemento evidenziato
                element.addEventListener('change', handleUserInteraction);
                element.addEventListener('click', () => setTimeout(handleUserInteraction, 50));
            });
        }
        if (isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 200));

            const targetCheckboxes = findCheckboxesInsideHighlightedElement(element);

            if (targetCheckboxes.length === 0) {
                console.warn("[AgenticTour] Nessuna checkbox rilevata all'interno dell'elemento evidenziato:", element);
                onComplete();
                return { data: null };
            }

            // Normalizzatore universale: conserva solo caratteri alfanumerici (elimina spazi, dash, en-dash, underscore, badge e punteggiatura)
            const superClean = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

            // SCENARIO 1: L'elemento evidenziato è una singola checkbox
            if (targetCheckboxes.length === 1) {
                checkTarget(targetCheckboxes[0]);
            }
            // SCENARIO 2: L'elemento evidenziato è un box con più opzioni
            else {
                const cleanTargetValue = superClean(value || '');

                if (cleanTargetValue && cleanTargetValue !== 'all') {
                    const getCheckboxMetadata = (cb: HTMLElement) => {
                        const dataTourAttr = cb.getAttribute('data-tour') || cb.closest('[data-tour]')?.getAttribute('data-tour') || '';

                        // Recupera il valore reale dall'input interno se presente (es. value="Italian_Serie_B_2026")
                        const inputVal = (cb instanceof HTMLInputElement)
                            ? cb.value
                            : cb.querySelector<HTMLInputElement>('input')?.value || '';

                        // Rimuove eventuali badge numerici finali dalla label (es. "Italian Serie B - 2026 (12)" -> "Italian Serie B - 2026")
                        const rawLabel = (cb.closest('label')?.textContent || cb.textContent || '').replace(/\s*\(\d+\)\s*$/, '');

                        return {
                            cleanDataTour: superClean(dataTourAttr),
                            cleanInputVal: superClean(inputVal),
                            cleanLabel: superClean(rawLabel)
                        };
                    };

                    // 1. CONTROLLO ESATTO
                    let matchedCheckboxes = targetCheckboxes.filter((cb) => {
                        const { cleanDataTour, cleanInputVal, cleanLabel } = getCheckboxMetadata(cb);

                        // A. Match esatto sul data-tour (la stringa finisce tassativamente con [valore] + 'library')
                        const isExactDataTour = cleanDataTour.endsWith(`${cleanTargetValue}library`);

                        // B. Match esatto sulla label a video
                        const isExactLabel = cleanLabel === cleanTargetValue;

                        // C. Match esatto sulla proprietà value dell'input HTML
                        const isExactInputValue = cleanInputVal === cleanTargetValue;

                        return isExactDataTour || isExactLabel || isExactInputValue;
                    });

                    // 2. CONTROLLO PARZIALE (Fallback solo se il controllo esatto non trova riscontri)
                    if (matchedCheckboxes.length === 0) {
                        matchedCheckboxes = targetCheckboxes.filter((cb) => {
                            const { cleanDataTour, cleanLabel, cleanInputVal } = getCheckboxMetadata(cb);
                            return (
                                cleanDataTour.includes(cleanTargetValue) ||
                                cleanLabel.includes(cleanTargetValue) ||
                                cleanInputVal.includes(cleanTargetValue)
                            );
                        });
                    }

                    // 3. Esecuzione del click
                    if (matchedCheckboxes.length > 0) {
                        matchedCheckboxes.forEach(checkTarget);
                    } else {
                        // Fallback finale: se nessun filtro trova corrispondenza, seleziona tutte le voci del box
                        targetCheckboxes.forEach(checkTarget);
                    }
                } else {
                    // Nessun valore target specificato: seleziona tutto il gruppo
                    targetCheckboxes.forEach(checkTarget);
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 400));
            onComplete();

            return { data: value };
        }
    }
    // DATA RANGE
    else if (dataType === 'date_range') {
        const inputEl = (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')
            ? element as HTMLInputElement
            : element.querySelector<HTMLInputElement>('input');

        // Helper per verificare se la data inserita dall'utente è completa
        const isDateCompleted = (val?: string) => {
            if (!val) return false;
            // Valido solo se non ci sono più placeholder di maschera
            return !val.includes('DD') && !val.includes('MM') && !val.includes('YYYY');
        };

        if (!isCertain) {
            return new Promise<{ data: string | null }>((resolve) => {

                // Stato iniziale
                updateButtons(isDateCompleted(inputEl?.value));

                const handleChange = (event: Event) => {
                    const target = event.target as HTMLInputElement;
                    const valid = isDateCompleted(target?.value);
                    updateButtons(valid);

                    if (valid) {
                        resolve({ data: target.value });
                    }
                };

                element.addEventListener('input', handleChange);
                element.addEventListener('change', handleChange);
            });
        }

        if (isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Esegue la digitazione sequenziale delle 16 cifre
            await simulateDateRangeTyping(element, value);

            await new Promise((resolve) => setTimeout(resolve, 500));
            onComplete();

            return { data: value };
        }

        return { data: null };
    }
        // =========================================================================
// DATA TYPE: TEXT (Search / Multiselect con Polling Attivo e Verifica Reale)
// =========================================================================
    else if (dataType === 'text') {
        // ---------------------------------------------------------------------
        // CASO 1: !isCertain -> L'utente digita e seleziona manualmente
        // ---------------------------------------------------------------------
        if (!isCertain) {
            return new Promise<{ data: string | null }>((resolve) => {
                const inputEl = (
                    element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
                        ? element
                        : element.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
                );

                updateButtons(Boolean(inputEl?.value.trim() && inputEl.value.trim().length > 0));

                const handleInput = (event: Event) => {
                    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
                    if (target) {
                        updateButtons(target.value.trim().length > 0);
                    }
                };

                const handleChange = (event: Event) => {
                    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
                    if (target && target.value) {
                        resolve({ data: target.value });
                    }
                };

                element.addEventListener('input', handleInput);
                element.addEventListener('change', handleChange);
            });
        }

        // ---------------------------------------------------------------------
        // CASO 2: isCertain === true -> Polling attivo e Click SOLO su opzione valida
        // ---------------------------------------------------------------------
        if (isCertain) {
            await new Promise((resolve) => setTimeout(resolve, 200));

            const activeInput = (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)
                ? element
                : element.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');

            // 1. Mette a fuoco l'input e simula l'apertura
            if (activeInput) {
                activeInput.focus();
                activeInput.click();
                activeInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', code: 'ArrowDown', keyCode: 40, bubbles: true }));
            }

            // 2. Scrive il valore per scatenare il filtro (es. "B" o "Italian Serie B")
            if (value) {
                simulateTyping(element, value);
            }

            const superClean = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
            const targetClean = superClean(value || '');

            const getItemText = (item: HTMLElement) => {
                const text = (item.textContent || '').replace(/\s*\(\d+\)\s*$/, '');
                const dataTourAttr = item.getAttribute('data-tour') || '';
                return {
                    cleanText: superClean(text),
                    cleanDataTour: superClean(dataTourAttr)
                };
            };

            // 3. RETRY LOOP ATTIVO: interroga il DOM finché l'opzione cercata non è visibile
            const timeoutMs = 4000;
            const startTime = Date.now();
            let matchedItems: HTMLElement[] = [];

            while (Date.now() - startTime < timeoutMs) {
                // Isolamento del Popper aperto
                let activePopper: HTMLElement | null = null;
                const listboxId = activeInput?.getAttribute('aria-controls');

                if (listboxId) {
                    activePopper = document.getElementById(listboxId);
                }

                if (!activePopper) {
                    const poppers = Array.from(document.querySelectorAll<HTMLElement>(
                        '.MuiAutocomplete-popper, .MuiPopover-root, [role="presentation"], .MuiMenu-paper'
                    ));
                    activePopper = poppers[poppers.length - 1] || null;
                }

                const searchContext = activePopper || element;

                const candidates = Array.from(
                    searchContext.querySelectorAll<HTMLElement>(
                        '.autocomplete-option, [role="option"], .MuiAutocomplete-option, .MuiMenuItem-root, [data-tour^="field_checkbox_filter"]'
                    )
                );

                if (candidates.length > 0 && targetClean) {
                    // A. Controllo Match Perfetto (===)
                    const exactMatches = candidates.filter((item) => {
                        const { cleanText, cleanDataTour } = getItemText(item);
                        return cleanText === targetClean || cleanDataTour.endsWith(`${targetClean}library`);
                    });

                    if (exactMatches.length > 0) {
                        matchedItems = exactMatches;
                        break; // Trovata l'opzione esatta: interrompe subito l'attesa
                    }

                    // B. Controllo Match Parziale (includes)
                    const partialMatches = candidates.filter((item) => {
                        const { cleanText, cleanDataTour } = getItemText(item);
                        return cleanText.includes(targetClean) || cleanDataTour.includes(targetClean);
                    });

                    // Se ci sono match parziali e non ci sono spinner di caricamento in corso
                    const isLoading = !!searchContext.querySelector('.MuiCircularProgress-root, .MuiAutocomplete-loading');
                    if (partialMatches.length > 0 && !isLoading) {
                        matchedItems = partialMatches;
                        break;
                    }
                }

                // Attesa di 100ms prima del prossimo tentativo per consentire a React di aggiornare il DOM
                await new Promise((r) => setTimeout(r, 100));
            }

            // 4. ESECUZIONE DELLA SPUNTA: clicca SOLO se abbiamo trovato corrispondenze reali
            if (matchedItems.length > 0) {
                for (const item of matchedItems) {
                    const nativeCheckbox = item.querySelector<HTMLInputElement>('input[type="checkbox"]') ||
                        (item instanceof HTMLInputElement && item.type === 'checkbox' ? item : null);

                    const isChecked = nativeCheckbox
                        ? nativeCheckbox.checked
                        : (
                            item.classList.contains('Mui-selected') ||
                            item.classList.contains('Mui-checked') ||
                            item.getAttribute('aria-selected') === 'true' ||
                            item.getAttribute('aria-checked') === 'true' ||
                            !!item.querySelector('.Mui-checked, [aria-checked="true"]')
                        );

                    // Clicca solo se non è già attiva
                    if (!isChecked) {
                        const clickable = item.querySelector<HTMLElement>('input, label, .MuiButtonBase-root') || item;
                        clickable.click();

                        if (nativeCheckbox && !nativeCheckbox.checked) {
                            nativeCheckbox.checked = true;
                            nativeCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
                        }

                        await new Promise((r) => setTimeout(r, 120));
                    }
                }
            } else {
                // SICUREZZA: se dopo 4s non esiste l'opzione "B", NON clicca elementi a caso come "A"
                console.warn(`[AgenticTour] Nessuna opzione corrispondente a "${value}" trovata nella tendina.`);
            }

            await new Promise((resolve) => setTimeout(resolve, 500));
            onComplete();

            return { data: value };
        }

        return { data: null };
    }
};
export const simulateDateRangeTyping = async (
    element: HTMLElement,
    dateRangeText: string // Arriva come "01/10/2026 - 15/10/2026"
): Promise<boolean> => {
    const input = (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')
        ? (element as HTMLInputElement)
        : element.querySelector<HTMLInputElement>('input');

    if (!input) {
        console.error("Nessun input trovato nell'elemento:", element);
        return false;
    }

    // 1. Parsing e validazione delle date
    const parts = dateRangeText.split(/\s*[-–—]\s*/);
    if (parts.length !== 2) {
        console.error("Formato data range non valido. Atteso 'DD/MM/YYYY - DD/MM/YYYY', ricevuto:", dateRangeText);
        return false;
    }

    const [startDate, endDate] = parts.map(p => p.trim());
    // MUI richiede tassativamente l'en-dash (\u2013) circondato da spazi
    const targetValue = `${startDate} \u2013 ${endDate}`;

    input.focus();
    input.select();
    await new Promise((r) => setTimeout(r, 150));

    // 2. Simula l'effetto digitazione visiva progressiva
    const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
    )?.set;

    // Maschera base visibile nell'input
    let currentDisplay = "DD/MM/YYYY \u2013 DD/MM/YYYY";
    const allChars = targetValue.split('');

    for (let i = 0; i < allChars.length; i++) {
        // Ricostruisce la stringa carattere per carattere
        currentDisplay = targetValue.slice(0, i + 1) + currentDisplay.slice(i + 1);
        nativeSetter?.call(input, currentDisplay);

        // Feedback visivo per l'utente (30ms a cifra)
        await new Promise((r) => setTimeout(r, 30));
    }

    // 3. Iniezione finale ufficiale tramite Paste Event (riconosciuto nativamente da MUI useField)
    const dataTransfer = new DataTransfer();
    dataTransfer.setData('text/plain', targetValue);

    const pasteEvent = new ClipboardEvent('paste', {
        clipboardData: dataTransfer,
        bubbles: true,
        cancelable: true,
        composed: true
    });
    input.dispatchEvent(pasteEvent);

    // 4. Fallback: imposta il valore reale finale e scatena gli eventi React
    nativeSetter?.call(input, targetValue);

    input.dispatchEvent(new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'insertFromPaste',
        data: targetValue
    }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    input.blur();
    return true;
};

export const getPreparedSteps = (steps : TourStep[]) : TourStep[] => {
    // Individuiamo l'indice del primo vero step visibile (quello senza locationCheck)
    const firstVisibleIndex = steps.findIndex(s => s?.locationCheck === undefined);

    // Mappiamo gli step definendo showButtons nativamente per ciascuno
    return steps.map((step, index) => {
        if (index === firstVisibleIndex) {
            return {
                ...step,
                popover: {
                    ...step.popover,
                    showButtons: ['close', 'next'] // Nasconde 'previous' solo sul primo step visibile
                }
            };
        }
        return step;
    });
}

export const getAllHelps = (guide : any, location : string) => {
    const currentPathname = location.split('?')[0];

    const segments = location.split('/').filter(Boolean);

    // IMPORTANTE PER CAPIRE DOVE SONO COME ULTIMO "/"
    const lastSegment = segments[segments.length - 1];

    const howTos = [], tutorials = [], relatedTutorials = []
    let description = ''

    if(guide.tourAssociations !== null) {
        const keys = Object.keys(guide.tourAssociations);


        for(const key of keys) {
            if(tourStepDict[guide.tourAssociations[key][0]]?.route === undefined) {
                if(key.startsWith('TUTORIAL'))
                    tutorials.push(key)
                else
                    howTos.push(key);
            }
            else {
                const stepRoute = tourStepDict[guide.tourAssociations[key][0]]?.route || '';
                const fullStepRoute = `${getSubRoot(currentPathname)}/${stepRoute}`.replace(/\/+/g, '/');
                if(fullStepRoute === currentPathname) {
                    if (key.startsWith('TUTORIAL'))
                        tutorials.push(key)
                    else
                        howTos.push(key);
                }
                else {
                    for(const val of guide.tourAssociations[key]) {
                        if(tourStepDict[val]?.route && `${getSubRoot(currentPathname)}/${stepRoute}`.replace(/\/+/g, '/') === currentPathname ) {
                            relatedTutorials.push(key)
                            break;
                        }
                    }
                }
                tourStepDict[guide.tourAssociations[key][0]]?.route !== undefined
            }
        }
    }
    if(guide.pageDescriptions !== null) {
        for(const pageKey of Object.keys(guide.pageDescriptions)) {
            if(pageKey.replace('/','') === lastSegment) {
                description = guide.pageDescriptions[pageKey];
                break;
            }
        }
    }

    return {howTos, tutorials, relatedTutorials, description};
}




export const waitForElement = (selector: string, timeout = 10000): Promise<Element> => {
    return new Promise((resolve, reject) => {
        // Se l'elemento c'è già, risolvi subito
        const el = document.querySelector(selector);
        if (el) return resolve(el);

        // Altrimenti, crea un observer che guarda i cambiamenti del DOM
        const observer = new MutationObserver(() => {
            const el = document.querySelector(selector);
            if (el) {
                observer.disconnect(); // Ferma l'osservatore
                resolve(el);
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        // 3. Imposta un timeout di sicurezza
        setTimeout(() => {
            observer.disconnect();
            reject(new Error(`Timeout attesa elemento: ${selector}`));
        }, timeout);
    });
};

//SIMULAZIONE DI SCRITTURA (non sapevo come fare altrimenti)
export const simulateTyping = (element: HTMLElement, text: string, finalEnterKey: boolean = false) => {
    const input = (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')
        ? element
        : element.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;

    if (!input) {
        console.error("Nessun input trovato nell'elemento:", element);
        return;
    }

    //TODO AGGIUNTA COPPIA DI FUNC
    input.focus();
    input.click();

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        Object.getPrototypeOf(input),
        "value"
    )?.set;

    nativeInputValueSetter?.call(input, text);

    // TODO Sequenza completa di eventi per triggerare ascoltatori basati su tastiera o input
    input.dispatchEvent(new KeyboardEvent('keydown', { bubbles: true, cancelable: true }));
    input.dispatchEvent(new Event('beforeinput', { bubbles: true, cancelable: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, cancelable: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    //C'ERA QUESTO PRIMA:
    //input.dispatchEvent(new Event('input', { bubbles: true }));
    //input.dispatchEvent(new Event('change', { bubbles: true }));

    if(finalEnterKey) {
        input.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            keyCode: 13,
            which: 13,
            bubbles: true,
            cancelable: true
        }));
    }

    return input;
};

// Helper per leggere il valore reale dell'input
export const getInputValue = (element: HTMLElement | Element | null): string => {
    if (!element) return "";

    // Se l'elemento stesso è già un input o textarea
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        return element.value;
    }

    // Cerca SOLO input visibili (esclude type="hidden") o le classi specifiche di MUI
    const input = element.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        'input:not([type="hidden"]), textarea, .MuiInputBase-input'
    );

    return input ? input.value : "";
};

export function makeBoxRequiredInputStep(stepConfig: TourStep, configRequirement: 'all' | 'one' = 'one'): TourStep {

    // Assicurati che checkListTextFields sia sempre un array
    const textFields = stepConfig.checkListTextFields || [];

    const validateFields = (): boolean => {
        const textFields = stepConfig.checkListTextFields || [];
        if (textFields.length === 0) return true;

        let filledCounter = 0;

        for (const selector of textFields) {
            const fieldParent = document.querySelector(selector);
            if (!fieldParent) continue;

            let hasValue = false;

            // 1. Controlla se c'è testo nell'input standard
            const visibleInputs = fieldParent.querySelectorAll('input:not([type="hidden"]), textarea');
            visibleInputs.forEach(el => {
                if ((el as HTMLInputElement).value.trim().length > 0) hasValue = true;
            });

            // 2. Controlla la presenza di Chip / Tag (Multichip)
            // Cerca le classi standard di MUI (.MuiChip-root) o classi custom che contengono "chip" o "tag"
            if (fieldParent.querySelector('.MuiChip-root, .MuiAutocomplete-tag, [class*="chip" i], [class*="tag" i]')) {
                hasValue = true;
            }

            // 3. Controlla eventuali input nascosti (spesso usati per mantenere il valore reale dietro le quinte)
            const hiddenInputs = fieldParent.querySelectorAll('input[type="hidden"]');
            hiddenInputs.forEach(el => {
                const val = (el as HTMLInputElement).value;
                // Controlla che non sia vuoto e che non sia un array vuoto "[]" (tipico di React)
                if (val && val.trim().length > 0 && val !== '[]') {
                    hasValue = true;
                }
            });

            if (hasValue) {
                filledCounter++;
            }
        }

        return configRequirement === 'all'
            ? filledCounter === textFields.length
            : filledCounter > 0;
    };

    const toggleNextButton = (enable: boolean) => {
        const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLElement | null;
        if (!nextBtn) return;

        if (enable) {
            nextBtn.classList.remove('disabled');
            nextBtn.removeAttribute('disabled');
            nextBtn.style.pointerEvents = 'auto';
            nextBtn.style.opacity = '1';
        } else {
            nextBtn.classList.add('disabled');
            nextBtn.setAttribute('disabled', 'true');
            nextBtn.style.pointerEvents = 'none';
            nextBtn.style.opacity = '0.4';
        }
    };

    return {
        ...stepConfig,
        onHighlightStarted: async (element?: Element) => {
            if (typeof stepConfig.onHighlightStarted === 'function') {
                await stepConfig.onHighlightStarted(element);
            }

            if (!element) return;

            if ((element as any)._tourInterval) {
                clearInterval((element as any)._tourInterval);
            }

            (element as any)._tourInterval = setInterval(() => {
                const isValid = validateFields();
                toggleNextButton(isValid);
            }, 250); // Messo a 250ms per non spammare troppo il DOM, 100ms è molto aggressivo
        },

        onDeselected: (element?: Element) => {
            if (typeof stepConfig.onDeselected === 'function') {
                stepConfig.onDeselected(element);
            }

            if (element && (element as any)._tourInterval) {
                clearInterval((element as any)._tourInterval);
                delete (element as any)._tourInterval;
            }

            toggleNextButton(true);
        },

        beforeNext: async (element: HTMLElement) => {
            if ((element as any)._tourInterval) {
                clearInterval((element as any)._tourInterval);
            }

            const isValid = validateFields();
            if (!isValid) return false;

            if (typeof stepConfig.beforeNext === 'function') {
                const res = await stepConfig.beforeNext(element);
                if (res === false) return false;
            }
        }
    };
}

function formatMatchWithoutDate(match: string) {
    const index = match.indexOf('/');

    if (index !== -1) {
        return match.substring(0, index).trimEnd();
    }

    return match;
}

export function makeRequiredInputStep (baseStep: TourStep, removeDate: boolean = false): TourStep {
    return {
        ...baseStep,
        onHighlightStarted: async (element: Element, step: any, options: any) => {
            if (typeof baseStep.onHighlightStarted === 'function') {
                await baseStep.onHighlightStarted(element, step, options);
            }

            if (!(element instanceof HTMLElement)) return;

            if (step?.isAgentic) {
                (element as any)._isAgenticStep = true;
            }

            if (step.isAgentic && step.agenticData?.agenticDataValue) {
                const dataType = step.agenticData.agenticDataType || 'text';

                let agenticDataValueFormatted = step.agenticData.agenticDataValue
                if(removeDate)
                    agenticDataValueFormatted = formatMatchWithoutDate(step.agenticData.agenticDataValue)

                const returnedInputValue = await executeAgenticInputSequence(
                    dataType,
                    element,
                    agenticDataValueFormatted,
                    step.agenticData.isCertain,
                    options.handleNext
                );

                return;
            }

            const inputEl = (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')
                ? element
                : element.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;

            if (!inputEl) return;

            if ((element as any)._tourInterval) {
                clearInterval((element as any)._tourInterval);
            }

            // =================================================================
            // WORKAROUND IMPERATIVO: Focus -> Svuotamento -> Apertura Forzata
            // VOLUTAMENTE COMMENTATO E REGISTRATO COSÌ COM'È. RATIONALE DIETRO: IL PROBLEMA È SOLO ALL'INIZIO, SIMULARE LA VISIBILITÀ
            // =================================================================
            setTimeout(() => {
                // 1. Focus vitale: MUI ignora ArrowDown se l'input non è attivo
                inputEl.focus();

                // 2. Svuotiamo/settiamo lo stato del testo come richiesto
                simulateTyping(element, "");

                // 3. ArrowDown Hack:
                // Aspettiamo 150ms affinché il ciclo di render di React (causato dal simulateTyping)
                // si concluda. Poi spariamo la Freccia Giù. MUI intercetterà l'evento
                // e aprirà la tendina bypassando totalmente i problemi di click e z-index.
                setTimeout(() => {
                    inputEl.dispatchEvent(new KeyboardEvent('keydown', {
                        key: 'ArrowDown',
                        code: 'ArrowDown',
                        keyCode: 40,
                        bubbles: true,
                        cancelable: true
                    }));
                }, 150);

            }, 200);

            (element as any)._tourInterval = setInterval(() => {
                const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLElement | null;
                if (!nextBtn) return;

                const hasText = getInputValue(element).trim().length > 0;
                const hasChip = !!element.querySelector('.MuiChip-root, .MuiAutocomplete-tag, [class*="chip" i], [class*="tag" i]');
                const isValid = hasText || hasChip;

                if (hasText) {
                    nextBtn.classList.remove('disabled');
                    nextBtn.removeAttribute('disabled');
                    nextBtn.style.pointerEvents = 'auto';
                    nextBtn.style.opacity = '1';
                } else {
                    nextBtn.classList.add('disabled');
                    nextBtn.setAttribute('disabled', 'true');
                    nextBtn.style.pointerEvents = 'none';
                    nextBtn.style.opacity = '0.4';
                }
            }, 100);

        },
        onDeselected: (element?: Element) => {
            if (typeof baseStep.onDeselected === 'function') {
                baseStep.onDeselected(element);
            }

            if (element && (element as any)._tourInterval) {
                clearInterval((element as any)._tourInterval);
                delete (element as any)._tourInterval;
            }

            const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLElement | null;
            if (nextBtn) {
                nextBtn.classList.remove('disabled');
                nextBtn.removeAttribute('disabled');
                nextBtn.style.pointerEvents = 'auto';
                nextBtn.style.opacity = '1';
            }
        },
        beforeNext: async (element: HTMLElement, step?: any) => {
            if ((element as any)._tourInterval) {
                clearInterval((element as any)._tourInterval);
            }

            if (step?.isAgentic || (element as any)._isAgenticStep) {
                delete (element as any)._isAgenticStep;
                return true;
            }

            // 1. Frena l'avanzamento se l'utente non ha scritto nulla
            const value = getInputValue(element);
            const hasChip = !!element.querySelector('.MuiChip-root, .MuiAutocomplete-tag, [class*="chip" i], [class*="tag" i]');

            if (value.trim().length === 0 && !hasChip) {
                return false;
            }

            // 2. Se l'input è valido, esegui l'eventuale primaNext personalizzato definito nello step
            if (typeof baseStep.beforeNext === 'function') {
                const res = await baseStep.beforeNext(element);
                if (res === false) return false;
            }

            return true;
        }
    };

};

export interface RequiredClickOptions {
    // Tasti da bloccare (fase capture) mentre lo step è attivo. Default: solo la freccia destra, che fa "Next"
    blockKeys?: string[];
}

/**
 * Trasforma uno step in uno step "l'utente DEVE cliccare l'elemento evidenziato".
 *
 * Attivo solo se nello step e' presente `requireUserClick: true`:
 * - `popover.disableButtons: ['next']` -> il bottone Next viene disabilitato nativamente da driver.js
 *   (classe .driver-popover-btn-disabled => opacita' ridotta + pointer-events: none).
 * - Blocco dei tasti: in driver.js 1.8 `allowKeyboardControl` viene letto SOLO dalla config globale
 *   (non dallo step, quindi metterlo nello step non serve a nulla). Per fermare la freccia destra
 *   aggiungiamo un listener in fase CAPTURE su document: quello di driver e' su window in fase BUBBLE,
 *   quindi stopImmediatePropagation lo blocca prima che venga emesso `arrowRightPress`.
 * - Avanzamento: dal click sull'elemento chiamiamo `opts.handleNext`, cioe' esattamente la funzione che
 *   TourContext passa agli hook e che gia' gestisce beforeNext + cambio rotta + destroy/re-drive.
 *   Non usiamo `advanceOnClick` di driver perche' il suo listener (document, bubble) salterebbe
 *   se l'app fa stopPropagation, e perche' insieme alla nostra chiamata farebbe doppio advanceOnClick.
 * - Guardia in beforeNext: se l'avanzamento arriva senza un click reale (event.isTrusted) sull'elemento
 *   evidenziato, viene bloccato. Cosi' un eventuale element.click() "programmatico" di un altro step
 *   non sblocca l'avanzamento, e il click reale viene contato una volta sola.
 *
 * NOTA: l'eventuale beforeNext/onHighlightStarted/onDeselected dello step originale vengono SEMPRE
 * eseguiti, e per onHighlightStarted la guardia qui dentro viene armata PRIMA (i listener sono sincroni,
 * cosi' un click non sfugge nemmeno se l'hook originale e' async).
 */
export function makeRequiredClickStep (stepConfig: TourStep, options: RequiredClickOptions = {}): TourStep {
    const requireUserClick = stepConfig.requireUserClick === true;
    const blockKeys = options.blockKeys ?? ['ArrowRight'];

    let userClicked = false;
    let activeElement: HTMLElement | null = null;
    let elementClickListener: ((event: MouseEvent) => void) | null = null;
    let keyListener: ((event: KeyboardEvent) => void) | null = null;

    // Idempotente: puo' essere chiamata piu' volte (onDeselected, rientro su uno step gia' visitato...)
    const detachListeners = () => {
        if (activeElement && elementClickListener) {
            activeElement.removeEventListener('click', elementClickListener, true);
        }
        if (keyListener) {
            document.removeEventListener('keydown', keyListener, true);
            document.removeEventListener('keyup', keyListener, true);
        }
        activeElement = null;
        elementClickListener = null;
        keyListener = null;
    };

    const advance = (opts?: any) => {
        if (typeof opts?.handleNext === 'function') {
            opts.handleNext();
            return;
        }
        // Fallback: usato solo se questo util viene usato fuori da TourContext
        opts?.driver?.moveNext?.();
    };

    return {
        ...stepConfig,

        // Senza questo l'elemento diventa "driver-no-interaction" (pointer-events: none) e il click dell'utente non arriva
        disableActiveInteraction: requireUserClick ? false : stepConfig.disableActiveInteraction,

        // L'avanzamento lo gestiamo noi dal click: advanceOnClick deve restare spento
        advanceOnClick: requireUserClick ? false : stepConfig.advanceOnClick,

        popover: {
            ...stepConfig.popover,
            ...(requireUserClick
                ? { disableButtons: Array.from(new Set([...(stepConfig.popover?.disableButtons ?? []), 'next'])) }
                : {})
        },

        onHighlightStarted: async (element?: Element, step?: any, opts?: any) => {
            detachListeners();
            userClicked = false;

            if (!(element instanceof HTMLElement)) return;

            if (step?.isAgentic && step?.agenticData) {
                const dataType = step.agenticData.agenticDataType || 'button';

                // L'agente prenderà il controllo: consentiamo l'avanzamento scavalcando il click utente
                userClicked = true;

                const returnedInputValue = await executeAgenticInputSequence(
                    dataType,
                    element,
                    step.agenticData.agenticDataValue || '',
                    step.agenticData.isCertain,
                    opts?.handleNext
                );
                return;
            }

            // La guardia va armata PRIMA di qualsiasi hook dello step
            if (requireUserClick) {
                activeElement = element;

                // Capture: intercetta anche i click sui figli (icone, span, MUI internals)
                elementClickListener = (event: MouseEvent) => {
                    // Solo click reali dell'utente: i click "programmatici" (element.click()) non sbloccano
                    if (!event.isTrusted) return;
                    // Doppio click prima che lo step cambi: la guardia e' gia' stata consumata
                    if (userClicked) return;

                    // 1. Sblocca la guardia di beforeNext (deve stare PRIMA, handleNext chiama beforeNext in sync)
                    userClicked = true;
                    // 2. Avanza
                    advance(opts);
                };
                element.addEventListener('click', elementClickListener, true);

                if (blockKeys.length > 0) {
                    keyListener = (event: KeyboardEvent) => {
                        if (!blockKeys.includes(event.key)) return;
                        event.preventDefault();
                        event.stopImmediatePropagation();
                    };
                    document.addEventListener('keydown', keyListener, true);
                    document.addEventListener('keyup', keyListener, true);
                }
            }

            if (typeof stepConfig.onHighlightStarted === 'function') {
                return stepConfig.onHighlightStarted(element, step, opts);
            }
        },

        onDeselected: (element?: Element, step?: any, opts?: any) => {
            detachListeners();
            userClicked = false;

            if (typeof stepConfig.onDeselected === 'function') {
                stepConfig.onDeselected(element, step, opts);
            }
        },
        beforeNext: async (element: HTMLElement) => {
            // Rete di sicurezza: senza click reale sull'elemento non si avanza
            if (requireUserClick && !userClicked) return false;

            if (typeof stepConfig.beforeNext === 'function') {
                const res = await stepConfig.beforeNext(element);
                if (res === false) return false;
            }
        }
    };
}